import { Add, AppRegistration } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Chip as MuiChip,
  Grid,
  Typography,
  TextField,
  MenuItem,
  TablePagination,
  Divider
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { InboundTripDto } from 'dtos'
import { InboundTripsParameters } from 'parameters'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import InboundTripsListCard from './InboundTripsListCard'
import InboundTripsAddEditDialog from './id/InboundTripsAddEditDialog'
import { enqueueSnackbar } from 'notistack'
import { DEBOUNCE_DELAY } from 'localConstants'

const sessionStorageKey = 'inboundTrips:parameters'

export default function InboundTrips() {
  const layout = useLayout()
  const navigate = useNavigate()
  //const [isLocked, setIsLocked] = useState(false)

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const defaultParameters: InboundTripsParameters = {
    page: 0,
    pageSize: 10,
    orderBy: 'inboundTripNumber',
    order: 'asc'
  }

  const [isCreatingInboundTrip, setIsCreatingInboundTrip] = useState<boolean>(false)
  const [isGettingInboundTrips, setIsGettingInboundTrips] = useState<boolean>(false)
  const [parameters, setParameters] = useState<InboundTripsParameters>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultParameters
  )
  const [inboundTrips, setInboundTrips] = useState<InboundTripDto[]>([])
  const [inboundTrip, setInboundTrip] = useState<InboundTripDto>(new InboundTripDto())
  // const [isInboundTripsSaving, setIsInboundTripsSaving] = useState<boolean>(false)
  const [InboundTripsAddEditDialogOpen, setInboundTripsAddEditDialogOpen] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const resultsStartRef = useRef<HTMLDivElement>(null)
  const shouldScrollToResultsRef = useRef<boolean>(false)

  const getInboundTrips = useDebounce((parameters: InboundTripsParameters) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(parameters))

    setIsGettingInboundTrips(true)
    controllers
      .getInboundTrips(parameters)
      .then(response => {
        setInboundTrips(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingInboundTrips(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    getInboundTrips(parameters)
  }, [getInboundTrips, parameters])

  useEffect(() => {
    layout.setTitle('Inbound Trips')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingInboundTrips)
  }, [layout, isGettingInboundTrips])

  useEffect(() => {
    if (isGettingInboundTrips || !shouldScrollToResultsRef.current) {
      return
    }

    shouldScrollToResultsRef.current = false
    window.requestAnimationFrame(() => {
      resultsStartRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
  }, [inboundTrips, isGettingInboundTrips])

  const handlePageChange = (_: unknown, page: number) => {
    shouldScrollToResultsRef.current = true
    setParameters(currentParameters => ({
      ...currentParameters,
      page
    }))
  }

  const renderPagination = () => (
    <TablePagination
      component='div'
      count={totalCount}
      onPageChange={handlePageChange}
      onRowsPerPageChange={e => {
        setParameters({
          ...parameters,
          page: 0,
          pageSize: e.target.value as unknown as number
        })
      }}
      page={parameters.page}
      rowsPerPage={parameters.pageSize}
      labelRowsPerPage={
        <Grid container spacing={1}>
          <Grid item>Order By:</Grid>

          <Grid item>
            <TextField
              InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
              onChange={e => {
                setParameters({ ...parameters, orderBy: e.target.value })
              }}
              select
              size='small'
              value={parameters.orderBy}
              variant='standard'
            >
              <MenuItem value='inboundTripNumber'>Inbound Trip #</MenuItem>

              <MenuItem value='inboundTransporterCompany'> Inbound Transporter Company</MenuItem>

              <MenuItem value='entryNumber'>Entry #</MenuItem>

              <MenuItem value='entryDate'>Entry Date</MenuItem>
            </TextField>
          </Grid>

          <Grid item>
            <TextField
              InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
              onChange={e => {
                setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
              }}
              select
              size='small'
              value={parameters.order}
              variant='standard'
            >
              <MenuItem value='asc'>A-Z</MenuItem>

              <MenuItem value='desc'>Z-A</MenuItem>
            </TextField>
          </Grid>

          <Grid item sx={{ alignSelf: 'stretch' }}>
            <Divider orientation='vertical' />
          </Grid>

          <Grid item>Cards Per Page:</Grid>
        </Grid>
      }
      rowsPerPageOptions={[
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 }
      ]}
      SelectProps={{ fullWidth: false }}
    />
  )

  return (
    <>
      <InboundTripsAddEditDialog
        initialValues={inboundTrip}
        loading={isCreatingInboundTrip}
        onClose={() => {
          setInboundTripsAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsCreatingInboundTrip(true)
          controllers
            .createInboundTrip(values)
            .then(_ => {
              enqueueSnackbar('Inbound Trip Created Successfully!', { variant: 'success' })
              getInboundTrips(parameters)
              setInboundTripsAddEditDialogOpen(false)
            })
            .finally(() => {
              setIsCreatingInboundTrip(false)
            })
        }}
        open={InboundTripsAddEditDialogOpen}
      />

      <Grid container spacing={1} alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Inbound Trips ({totalCount!})
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm='auto' alignContent='center'>
                  <Grid item container xs={12}>
                    <ListFilterGroup>
                      <TextField
                        fullWidth
                        label='Filter Field'
                        margin='dense'
                        onChange={e => {
                          setFilterField(e.target.value)
                          setFilterValue('')
                        }}
                        select
                        size='small'
                        sx={{ minWidth: 200 }}
                        value={filterField || ''}
                      >
                        <MenuItem value='inboundTripNumber'>Inbound Trip #</MenuItem>

                        <MenuItem value='inboundTransporterCompany'>Inbound Transporter Company</MenuItem>

                        <MenuItem value='exporterCompany'>Exporter Company</MenuItem>

                        <MenuItem value='buyerCompany'>Buyer Company</MenuItem>

                        <MenuItem value='entryNumber'>Entry #</MenuItem>

                        <MenuItem value='entryDate'>Entry Date</MenuItem>

                        <MenuItem value='papsNumber'>PAPS #</MenuItem>

                        <MenuItem value='vin'>VIN</MenuItem>

                        <MenuItem value='year'>Year</MenuItem>

                        <MenuItem value='make'>Make</MenuItem>

                        <MenuItem value='model'>Model</MenuItem>
                      </TextField>

                      <Grid container>
                        <Grid item xs={12} sm='auto'>
                          <TextField
                            disabled={!filterField}
                            label='Filter Value'
                            margin='dense'
                            onChange={e => {
                              setFilterValue(e.target.value)
                            }}
                            size='small'
                            InputLabelProps={{
                              shrink: filterField === 'entryDate' || Boolean(filterValue)
                            }}
                            type={filterField === 'entryDate' ? 'date' : filterField === 'year' ? 'number' : 'text'}
                            value={filterValue || ''}
                          />
                        </Grid>
                      </Grid>

                      <Button
                        color='inherit'
                        disabled={!filterField || !filterValue}
                        size='medium'
                        variant='contained'
                        onClick={() => {
                          setParameters({
                            ...parameters,
                            [filterField]: filterValue
                          })

                          setFilterField('')
                          setFilterValue('')
                        }}
                      >
                        APPLY FILTER
                      </Button>

                      <Button
                        color='secondary'
                        disabled={
                          parameters.orderBy === defaultParameters.orderBy &&
                          parameters.order === defaultParameters.order &&
                          parameters.inboundTripNumber === defaultParameters.inboundTripNumber &&
                          parameters.inboundTransporterCompany === defaultParameters.inboundTransporterCompany &&
                          parameters.exporterCompany === defaultParameters.exporterCompany &&
                          parameters.buyerCompany === defaultParameters.buyerCompany &&
                          parameters.entryNumber === defaultParameters.entryNumber &&
                          parameters.entryDate === defaultParameters.entryDate &&
                          parameters.papsNumber === defaultParameters.papsNumber &&
                          parameters.vin === defaultParameters.vin &&
                          parameters.year === defaultParameters.year &&
                          parameters.make === defaultParameters.make &&
                          parameters.model === defaultParameters.model
                        }
                        size='medium'
                        variant='text'
                        onClick={() => {
                          setParameters(defaultParameters)

                          setFilterField('')
                          setFilterValue('')
                        }}
                      >
                        RESET FILTERS
                      </Button>
                    </ListFilterGroup>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm='auto' container alignItems='center'>
                  <Grid item>
                    <Button
                      color='primary'
                      endIcon={<Add />}
                      onClick={() => {
                        setInboundTrip(new InboundTripDto())
                        setInboundTripsAddEditDialogOpen(true)
                      }}
                      variant='text'
                    >
                      ADD NEW
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {parameters.inboundTripNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Inbound Trip # contains "${parameters.inboundTripNumber}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            inboundTripNumber: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.inboundTransporterCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Inbound Transporter Company contains "${parameters.inboundTransporterCompany}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            inboundTransporterCompany: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.exporterCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Exporter Company contains "${parameters.exporterCompany}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            exporterCompany: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.buyerCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Buyer Company contains "${parameters.buyerCompany}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            buyerCompany: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.entryNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Entry # contains "${parameters.entryNumber}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            entryNumber: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.entryDate && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Entry Date is ${formatDate(parameters.entryDate)}`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            entryDate: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.papsNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`PAPS # contains "${parameters.papsNumber}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            papsNumber: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.vin && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`VIN contains "${parameters.vin}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            vin: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.year && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Year is ${parameters.year}`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            year: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.make && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Make contains "${parameters.make}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            make: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.model && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Model contains "${parameters.model}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            model: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} ref={resultsStartRef} sx={{ scrollMarginTop: '64px' }}>
          {renderPagination()}
        </Grid>

        {inboundTrips.map(inboundTrip => (
          <Grid item xs={12} key={inboundTrip.inboundTripGuid}>
            <InboundTripsListCard inboundTrip={inboundTrip}>
              <Button
                endIcon={<AppRegistration />}
                onClick={() => {
                  navigate(`/inbound-trips/` + inboundTrip.inboundTripGuid)
                }}
              >
                MANAGE TRIP
              </Button>
            </InboundTripsListCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <ExplanationAccordion>
            || The Inbound Trips table will contain grouping so that an Inbound Trip# is displayed once, each PAPS# is displayed
            once, and then there is a row for each VIN. || Each Inbound Trip displays this information on the left side of the
            card: Inbound Trip#, Inbound Transporter Company, Buyer (nullable), Entry #, and Entry Date. || Pressing the “Manage
            Trip” button opens the Inbound Trip Details screen for that Inbound Trip. || Each PAPS will have its own section on
            the Inbound Trip’s card displaying the PAPS# and Exporter as well as each VIN within the PAPS, showing this
            information: VIN, Year, Make, and Model || If the PAPS is locked, there will be red bold text in the header section
            saying, “PAPS LOCKED”. || The “Filter Field” dropdown contains these options: Inbound Trip#, Inbound Transporter
            Company, Exporter, Buyer, Entry #, Entry Date, PAPS#, VIN, Year, Make, and Model. A user may enter their search
            criteria into the “Filter Value” and then press the “APPLY FILTER” button to show the matching results. || Status
            chips will appear on each Inbound Trip and multiple chips could appear at the same time for a trip: Unlocked
            (Inbound Trip is not locked), Locked (Inbound Trip is locked), Sent to Broker (Inbound Trip documents have been Sent
            to Broker / Transporter), Checked In (at least one VIN associated with the Inbound Trip has had a Vehicle Check-In
            photo uploaded).||
          </ExplanationAccordion>
        </Grid>

        <Grid item xs={12}>
          {renderPagination()}
        </Grid>
      </Grid>
    </>
  )
}
